import React from 'react';
import { Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../common/Button';
import { Interview } from '../../types/interviews';
import Modal from '../common/Modal';

interface InterviewCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  interviewDetails: Interview;
  isLoading?: boolean;
}

export const InterviewCancelModal: React.FC<InterviewCancelModalProps> = React.memo(({
  isOpen,
  onClose,
  onCancel,
  interviewDetails,
  isLoading = false
}) => {
  // Format the date to a readable format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle confirmation by calling onCancel callback
  const handleConfirm = () => {
    onCancel();
  };

  // Use the improved Modal component
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Cancel Interview"
      maxWidth="450px"
    >
      <div className="p-5">
        <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4">
          Are you sure you want to cancel this interview? This action cannot be undone.
        </p>
        
        {/* Interview Details */}
        <div className="bg-background-light/70 dark:bg-background-dark/70 p-4 rounded-xl border border-border-light dark:border-border-dark mb-5 animate-navbar-item-fade-in">
          <h4 className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
            {interviewDetails.title}
          </h4>
          
          <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
            <Calendar size={14} className="mr-2 text-primary-light dark:text-primary-dark" />
            <span>{formatDate(interviewDetails.date)}</span>
          </div>
          
          <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            <span className="text-text-primary-light dark:text-text-primary-dark font-medium">
              {interviewDetails.company}
            </span>
            {' - '}
            <span>{interviewDetails.jobRole}</span>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            No, Keep Interview
          </Button>
          
          <Button 
            variant="primary" 
            onClick={handleConfirm}
            className="w-full sm:w-auto !bg-red-500 hover:!bg-red-600 !border-red-500 !shadow-red-500/30"
            loading={isLoading}
          >
            {isLoading ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </div>
      </div>
    </Modal>
  );
});

InterviewCancelModal.displayName = 'InterviewCancelModal';

export default InterviewCancelModal; 